import { MinusIcon, XIcon } from '@heroicons/react/outline';

import clsxm from '@/lib/clsxm';

import {
  CheckGlyph,
  SectionHeading,
  tone,
} from '@/components/agents/primitives';

import {
  formatMoney,
  type PlanPrice,
  QUIET,
  TWILIO_PER_MINUTE,
  useFormatLocale,
  usePlanPrice,
  useReceptionistT,
} from './kit';

type Verdict = 'yes' | 'partly' | 'no';
type Cell = { verdict?: Verdict; text: string };
type Row = { label: string; cells: [Cell, Cell, Cell] };

/**
 * An honest side-by-side: what the AI receptionist does well, and the calls
 * a person still handles better (it can't transfer a call yet).
 */
export default function ReceptionistCompare({ price }: { price: PlanPrice }) {
  const { t } = useReceptionistT();
  const locale = useFormatLocale();
  const plan = usePlanPrice(price);
  const perMinute = formatMoney(TWILIO_PER_MINUTE, 'usd', locale, 2);

  const columns = [
    t('AI receptionist'),
    t('Human receptionist'),
    t('Answering service'),
  ];

  const rows: Row[] = [
    {
      label: t('Picks up at night, on weekends and on holidays'),
      cells: [
        { verdict: 'yes', text: t('Always') },
        { verdict: 'no', text: t('During office hours') },
        { verdict: 'yes', text: t('Often, depending on the plan') },
      ],
    },
    {
      label: t('Knows your prices, services and policies'),
      cells: [
        {
          verdict: 'yes',
          text: t('Learns them from your website and documents'),
        },
        { verdict: 'yes', text: t('After training') },
        { verdict: 'partly', text: t('From a script you provide') },
      ],
    },
    {
      label: t('Books appointments into your calendar'),
      cells: [
        { verdict: 'yes', text: t('Into Google Calendar') },
        { verdict: 'yes', text: t('Yes') },
        { verdict: 'partly', text: t('Some do') },
      ],
    },
    {
      label: t('Transfers a call to you'),
      cells: [
        { verdict: 'no', text: t('Not yet. It takes a message instead') },
        { verdict: 'yes', text: t('Yes') },
        { verdict: 'yes', text: t('Usually') },
      ],
    },
    {
      label: t('Handles unusual or sensitive calls'),
      cells: [
        { verdict: 'partly', text: t('Follows your instructions') },
        { verdict: 'yes', text: t('With judgment') },
        { verdict: 'partly', text: t('It varies') },
      ],
    },
    {
      label: t('What you pay'),
      cells: [
        {
          text: plan
            ? t(
                'From {{price}} a month, plus Twilio’s minutes: about {{perMinute}} a minute in the US',
                { price: plan, perMinute },
              )
            : t(
                'A paid plan, plus Twilio’s minutes: about {{perMinute}} a minute in the US',
                { perMinute },
              ),
        },
        { text: t('A salary') },
        { text: t('A monthly fee, often charged per minute or per call') },
      ],
    },
  ];

  return (
    <section id='compare' className='py-20 sm:py-28'>
      <div className='mx-auto max-w-7xl px-6 lg:px-8'>
        <SectionHeading
          title={t('AI receptionist or a person?')}
          tail={t('An honest comparison.')}
          lead={t(
            'A person is still better at some calls. Here is where each option fits, so you can decide which calls to hand over.',
          )}
        />

        {/* Phones and tablets get one card per question; a table from lg up. */}
        <div className='mt-14 grid gap-4 sm:grid-cols-2 lg:hidden'>
          {rows.map(({ label, cells }) => (
            <div
              key={label}
              className={clsxm('rounded-[22px] p-5', tone.surface, tone.line)}
            >
              <h3 className={clsxm('text-base font-semibold', tone.ink)}>
                {label}
              </h3>
              <dl className='mt-4 space-y-3'>
                {cells.map((cell, index) => (
                  <div key={columns[index]} className='flex gap-3'>
                    <VerdictMark verdict={cell.verdict} />
                    <div className='min-w-0'>
                      <dt
                        className={clsxm(
                          'text-xs font-semibold',
                          index === 0
                            ? 'text-primary-700 dark:text-primary-300'
                            : QUIET,
                        )}
                      >
                        {columns[index]}
                      </dt>
                      <dd className={clsxm('text-[14px] leading-6', tone.body)}>
                        {cell.text}
                      </dd>
                    </div>
                  </div>
                ))}
              </dl>
            </div>
          ))}
        </div>

        <div
          className={clsxm(
            'relative mt-14 hidden overflow-hidden rounded-[28px] lg:block',
            tone.surface,
            tone.line,
          )}
        >
          <table className='w-full table-fixed border-collapse text-start'>
            <caption className='sr-only'>
              {t(
                'AI receptionist, human receptionist and answering service compared',
              )}
            </caption>
            <colgroup>
              <col className='w-[31%]' />
              <col className='w-[25%]' />
              <col className='w-[22%]' />
              <col className='w-[22%]' />
            </colgroup>
            <thead>
              <tr>
                <td className='px-6 py-5' />
                {columns.map((column, index) => (
                  <th
                    key={column}
                    scope='col'
                    className={clsxm(
                      'px-5 py-5 text-start text-[15px] font-semibold',
                      index === 0
                        ? 'bg-primary-50 text-primary-700 dark:bg-primary-400/10 dark:text-primary-300'
                        : tone.ink,
                    )}
                  >
                    {column}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map(({ label, cells }) => (
                <tr
                  key={label}
                  className='border-t border-[#0f0f28]/[0.07] dark:border-white/[0.07]'
                >
                  <th
                    scope='row'
                    className={clsxm(
                      'px-6 py-5 text-start align-top text-[15px] font-semibold leading-6',
                      tone.ink,
                    )}
                  >
                    {label}
                  </th>
                  {cells.map((cell, index) => (
                    <td
                      key={columns[index]}
                      className={clsxm(
                        'px-5 py-5 align-top',
                        index === 0 &&
                          'bg-primary-50/70 dark:bg-primary-400/[0.06]',
                      )}
                    >
                      <span className='flex gap-2.5'>
                        <VerdictMark verdict={cell.verdict} />
                        <span
                          className={clsxm(
                            'min-w-0 text-[14px] leading-6',
                            index === 0 ? tone.ink : tone.body,
                          )}
                        >
                          {cell.text}
                        </span>
                      </span>
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </section>
  );
}

function VerdictMark({ verdict }: { verdict?: Verdict }) {
  const { t } = useReceptionistT();
  if (!verdict) return <span className='w-5 flex-none' aria-hidden='true' />;

  const look = {
    yes: 'bg-emerald-500 text-white',
    partly: 'bg-amber-400/20 text-amber-700 dark:text-amber-300',
    no: 'bg-[#0f0f28]/[0.07] text-[#6f6f86] dark:bg-white/10 dark:text-[#a4a4bd]',
  }[verdict];
  const label = { yes: t('Yes'), partly: t('Partly'), no: t('No') }[verdict];

  return (
    <span
      className={clsxm(
        'mt-0.5 flex h-5 w-5 flex-none items-center justify-center rounded-full',
        look,
      )}
    >
      <span className='sr-only'>{label}</span>
      {verdict === 'yes' && <CheckGlyph className='h-3 w-3' />}
      {verdict === 'partly' && (
        <MinusIcon aria-hidden='true' className='h-3 w-3' />
      )}
      {verdict === 'no' && <XIcon aria-hidden='true' className='h-3 w-3' />}
    </span>
  );
}
